/** Frontend wrapper for /bot-tasks: jobs given to a workspace bot and run as
 * Claude Managed Agents sessions. A task belongs to the person who gave it. */
import { API_BASE, getAuthHeaders } from '../utils/supabase/client';
import { throwApiError } from './BotApiError';

export type BotTaskStatus = 'starting' | 'running' | 'needs_approval' | 'waiting' | 'completed' | 'failed' | 'cancelled';

/** A file the bot made. Names come from a model: show as text only. */
export interface BotTaskFile {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  /** Anything other than stored, skipped or expired is ignored by the card. */
  status: 'stored' | 'skipped' | 'expired' | (string & {});
}

export type BotTaskDeliverable = 'xlsx' | 'docx' | 'pdf' | 'csv';

/** How the grader judged the bot's file. Unknown `result` values show nothing extra. */
export interface BotTaskOutcome {
  result: 'satisfied' | 'needs_revision' | 'max_iterations_reached' | 'failed' | 'interrupted' | 'budget_reached' | (string & {});
  /** Check round, from 1. */
  iterations: number;
  /** Model-written: show as plain text only. */
  explanation: string;
  /** Server-written line, shown as given. */
  note: string | null;
}

export interface BotTask {
  id: string;
  botId: string;
  botName: string | null;
  workspaceId: string;
  prompt: string;
  source: 'app' | 'quantis_ai' | 'schedule';
  /** Set when a schedule started this task. */
  scheduleId?: string | null;
  scheduledFor?: string | null;
  status: BotTaskStatus;
  messages: { role: 'person' | 'agent'; text: string; at: string }[];
  /** Changes the bot asked to make, waiting for this person's decision. */
  pendingActions: { toolUseId: string; name: string; summary: string; at?: string }[];
  resultText: string | null;
  /** Absent when the bot made none or the feature is off. */
  files?: BotTaskFile[];
  /** Absent on older servers and plain tasks. */
  deliverable?: string | null;
  outcome?: BotTaskOutcome | null;
  /** Parallel parts the bot split the job into. Absent or 0 when it did not. */
  parts?: number;
  /** Only present when true: the verdict was cleared by a follow-up. */
  outcomeStale?: boolean;
  error: string | null;
  /** List-price spend so far, in US cents, as a string. */
  listCostCents: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

/** Still moving, so worth refreshing. */
export const isBotTaskActive = (task: Pick<BotTask, 'status'>) => ['starting', 'running', 'needs_approval', 'waiting'].includes(task.status);

const BASE = `${API_BASE}/bot-tasks`;

async function readError(res: Response): Promise<string> {
  try {
    const data = await res.json();
    return (data && (data.error || data.message)) || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

async function send(path: string, init: RequestInit = {}): Promise<BotTask> {
  const res = await fetch(`${BASE}${path}`, { ...init, headers: await getAuthHeaders({ 'Content-Type': 'application/json' }) });
  if (!res.ok) return throwApiError(res);
  return (await res.json()).task;
}

export const botTasksService = {
  /** `requestId` names one start attempt: the transport's retries and the
   * person pressing Start again after a lost reply both reuse it, so the
   * server starts one task, not several. */
  create: (input: { botId: string; workspaceId: string; prompt: string; requestId?: string; deliverable?: BotTaskDeliverable }) =>
    send('', { method: 'POST', body: JSON.stringify({ ...input, requestId: input.requestId ?? crypto.randomUUID() }) }),
  /** `sync` asks the server to read the session even for a completed task,
   * which is how a follow-up whose reply was lost is found. */
  get: (id: string, options: { sync?: boolean } = {}) => send(`/${encodeURIComponent(id)}${options.sync ? '?sync=1' : ''}`),
  decide: (id: string, toolUseId: string, approve: boolean) =>
    send(`/${encodeURIComponent(id)}/actions/${encodeURIComponent(toolUseId)}`, { method: 'POST', body: JSON.stringify({ approve }) }),
  message: (id: string, text: string) => send(`/${encodeURIComponent(id)}/messages`, { method: 'POST', body: JSON.stringify({ text }) }),
  cancel: (id: string) => send(`/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),
  /** A fresh link, valid for 60 seconds. Never cache it. */
  async fileUrl(taskId: string, fileId: string): Promise<string> {
    const res = await fetch(`${BASE}/${encodeURIComponent(taskId)}/files/${encodeURIComponent(fileId)}`, { headers: await getAuthHeaders() });
    if (res.status === 404) throw new Error('This file is no longer available.');
    if (!res.ok) throw new Error(await readError(res));
    const data = await res.json();
    if (typeof data?.url !== 'string' || !data.url) throw new Error('This file is no longer available.');
    return data.url;
  },
  async list(workspaceId: string, botId?: string): Promise<BotTask[]> {
    const query = new URLSearchParams({ workspaceId, ...(botId ? { botId } : {}) });
    const res = await fetch(`${BASE}?${query}`, { headers: await getAuthHeaders() });
    if (!res.ok) throw new Error(await readError(res));
    const data = await res.json();
    return Array.isArray(data?.tasks) ? data.tasks : [];
  },
};
